import React, { useRef } from 'react';
import type { UnitFiltersForm } from '@mark43/cad-api';
import { useAsyncResponsePlan } from '../use-async-response-plan';
import {
    AsyncResponsePlanContext,
    AsyncResponsePlanContextValue,
} from './async-response-plan-context';

const EMPTY_FILTERS: UnitFiltersForm = {
    dispatchAreaIds: [],
    emsUnitTypeIds: [],
    equipmentIds: [],
    fireUnitTypeIds: [],
    policeUnitTypeIds: [],
    skillIds: [],
};

/**
 * Mounts the async response-plan engine for ONE Manage Units panel and exposes it DOWN via context.
 * This component is the only place `useAsyncResponsePlan` runs. The host mounts it only when
 * `asyncResponsePlansEnabled` is true. Flag-off it is not rendered: no kick, no
 * `responsePlanGenerated` subscription.
 *
 * The two refs are owned here (above the store) and written by the store below, then read by the kick
 * at fire-time:
 *  - `selectedUnitIdsRef`: the store's effective selection. The kick carries it so the backend keeps
 *    already-queued units across filter changes.
 *  - `filtersRef`: the store's current `selectedFilters`.
 * Both are refs because the provider cannot read these values (the store owns them below it) and must
 * not re-render when they change; the kick reads the live `.current` post-render.
 *
 * `value` is intentionally rebuilt each render: every field that changes is a real async-state change
 * (status / first-load / landed plan), so a memo would not reduce consumer re-renders. The hook's
 * outputs stay reference-stable when unchanged (e.g. `initiallySelectedUnits` is a shared empty const
 * until a plan lands), which keeps the store's plan-merge memo stable and avoids a Maximum update depth
 * error from an unstable dependency.
 *
 * No PII/CJI flows through — `useAsyncResponsePlan` handles only opaque ids + status.
 */
export function AsyncResponsePlanProvider({
    eventId,
    children,
}: {
    eventId: number;
    // Optional so a test can construct this via `React.createElement(Provider, { eventId }, child)`,
    // satisfying `react/no-children-prop` without TS requiring `children` in the props object. In
    // production the host always passes children via JSX.
    children?: React.ReactNode;
}): React.ReactElement {
    const selectedUnitIdsRef = useRef<number[]>([]);
    const filtersRef = useRef<UnitFiltersForm>(EMPTY_FILTERS);

    const asyncResponsePlan = useAsyncResponsePlan({
        eventId,
        filtersRef,
        // always true here: this provider mounts only when asyncResponsePlansEnabled is true (see host)
        asyncEnabled: true,
        selectedUnitIdsRef,
    });

    const value: AsyncResponsePlanContextValue = {
        recommendedUnitIds: asyncResponsePlan.recommendedUnitIds,
        landedRecommendedUnits: asyncResponsePlan.initiallySelectedUnits,
        responsePlanV2: asyncResponsePlan.responsePlanV2,
        initialPrimaryUnitId: asyncResponsePlan.initialPrimaryUnitId,
        initiallySelectedUnits: asyncResponsePlan.initiallySelectedUnits,
        status: asyncResponsePlan.status,
        isFirstLoad: asyncResponsePlan.isFirstLoad,
        isRefreshing: asyncResponsePlan.isRefreshing,
        requestInitialKick: asyncResponsePlan.requestInitialKick,
        rekick: asyncResponsePlan.rekick,
        retry: asyncResponsePlan.retry,
        selectedUnitIdsRef,
        filtersRef,
    };

    return (
        <AsyncResponsePlanContext.Provider value={value}>
            {children}
        </AsyncResponsePlanContext.Provider>
    );
}
